import validator from 'validator';
import { safeParse } from 'secure-json-parse';
import { Verdict } from '@investigator/shared/generated/graphql';
import { AIInsightVerdict } from '../types';
import {
  CommunicatingEntityDetail,
  CommunicationDirection,
} from '../types/entityCommunication';

const ipaddr = require('ipaddr.js');

/**
 * Determines if an entity name represents a public IP address
 * @param entityName - The entity name to check
 * @returns true if the entity is a public IP address, false otherwise
 */
export const isPublicIPAddress = (entityName?: string): boolean => {
  if (!entityName || !validator.isIP(entityName)) {
    return false;
  }

  try {
    const parsed = ipaddr.parse(entityName);
    return parsed?.range() !== 'private';
  } catch {
    return false;
  }
};

/**
 * Generates the modal title with entity name or fallback
 * @param entityName - The entity name to include in the title
 * @param template - The title template string with {0} placeholder
 * @param fallback - The fallback title if no entity name is provided
 * @returns The formatted title string
 */
export const getEntityModalTitle = (
  entityName: string | undefined,
  template: string,
  fallback: string,
): string => {
  return entityName ? template.replace('{0}', entityName) : fallback;
};

/**
 * Converts AI insight verdict enum values to user-friendly display strings
 * @param verdict - The AI insight verdict enum value
 * @returns The user-friendly display string for the verdict
 */
export const getAIInsightVerdictDisplay = (
  verdict: AIInsightVerdict,
): string => {
  const displayMap: Record<AIInsightVerdict, string> = {
    [AIInsightVerdict.LIKELY_MALICIOUS]: 'Likely malicious',
    [AIInsightVerdict.LIKELY_BENIGN]: 'Likely benign',
    [AIInsightVerdict.SUSPICIOUS]: 'Suspicious',
    [AIInsightVerdict.UNDETERMINED]: 'Undetermined',
  };
  return displayMap[verdict];
};

/**
 * Processes aggregated entity communication events from groupBy query
 * @param events - Array of event strings from the aggregate query
 * @returns Object containing arrays of inbound and outbound entities
 */
export const processAggregatedEntityCommunicationEvents = (
  events: string[],
): {
  inboundEntities: CommunicatingEntityDetail[];
  outboundEntities: CommunicatingEntityDetail[];
} => {
  const inbound: CommunicatingEntityDetail[] = [];
  const outbound: CommunicatingEntityDetail[] = [];

  events.forEach((eventStr: string) => {
    const event = safeParse(eventStr);
    if (!event) return;

    const role = event['role'] || '';
    const otherIp = event['other_ip'] || '';

    // Humio's selectLast() creates fields with underscore prefixes
    const otherMac = event['_selectLast_other_mac'] || event['other_mac'] || '';

    if (!otherIp || role === 'unknown') {
      return;
    }

    // Parse timestamp - check both selectLast format and direct format
    // Humio's @timestamp is in milliseconds
    let timestamp = 0;
    const timestampValue =
      event['_selectLast_@timestamp'] ||
      event['@timestamp'] ||
      event['timestamp'];

    if (timestampValue) {
      if (typeof timestampValue === 'number') {
        timestamp = timestampValue;
      } else if (typeof timestampValue === 'string') {
        const parsed = new Date(timestampValue).getTime();
        timestamp = isNaN(parsed) ? 0 : parsed;
      }
    }

    const port =
      Number(
        event['_selectLast_id.resp_p'] || event['id.resp_p'] || event['port'],
      ) || 0;
    const protocol =
      event['_selectLast_proto'] || event['proto'] || event['protocol'] || '';
    const count = Number(event['_count'] || event['count']) || 1;

    const detail: CommunicatingEntityDetail = {
      timestamp,
      entityIp: otherIp,
      macAddress: otherMac || undefined,
      count,
      port,
      protocol,
      direction:
        role === 'as_server'
          ? CommunicationDirection.INBOUND
          : CommunicationDirection.OUTBOUND,
    };

    // as_server means current entity was the server, so other entity contacted us (inbound)
    // as_client means current entity was the client, so we contacted other entity (outbound)
    if (role === 'as_server') {
      inbound.push(detail);
    } else if (role === 'as_client') {
      outbound.push(detail);
    }
  });

  return {
    inboundEntities: inbound,
    outboundEntities: outbound,
  };
};

/**
 * Maps Verdict from the agentic triage report API to AIInsightVerdict enum
 * @param verdict - The Verdict from the backend API (or string value)
 * @returns The corresponding AIInsightVerdict enum value for UI display
 *
 * Note: This function handles both the typed Verdict enum and raw string values
 * to support backward compatibility with older 'LOW_CONFIDENCE' values.
 */
export const mapVerdictToAIInsightVerdict = (
  verdict: Verdict | string,
): AIInsightVerdict => {
  // Handle string values directly (for backward compatibility)
  if (typeof verdict === 'string') {
    const stringMapping: Record<string, AIInsightVerdict> = {
      LIKELY_MALICIOUS: AIInsightVerdict.LIKELY_MALICIOUS,
      LIKELY_BENIGN: AIInsightVerdict.LIKELY_BENIGN,
      LOW_CONFIDENCE: AIInsightVerdict.LIKELY_BENIGN, // Backward compatibility
      SUSPICIOUS: AIInsightVerdict.SUSPICIOUS,
      UNDETERMINED: AIInsightVerdict.UNDETERMINED,
    };
    return stringMapping[verdict] || AIInsightVerdict.UNDETERMINED;
  }

  // Handle typed Verdict enum
  const mapping: Record<Verdict, AIInsightVerdict> = {
    [Verdict.LikelyMalicious]: AIInsightVerdict.LIKELY_MALICIOUS,
    [Verdict.LikelyBenign]: AIInsightVerdict.LIKELY_BENIGN,
    [Verdict.Suspicious]: AIInsightVerdict.SUSPICIOUS,
    [Verdict.Undetermined]: AIInsightVerdict.UNDETERMINED,
  };
  return mapping[verdict] || AIInsightVerdict.UNDETERMINED;
};

export const getTruncatedText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  return lastSpace > maxLength * 0.8
    ? truncated.substring(0, lastSpace)
    : truncated;
};

export function getClampedPageIndex(input: number, pageCount: number): number {
  const newIndex = input > 0 ? input - 1 : 0;
  return Math.min(Math.max(newIndex, 0), pageCount - 1);
}
